import { cva } from 'class-variance-authority';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  X,
  TriangleAlert,
} from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils';

const notificationBannerVariants = cva(
  'relative flex w-full justify-center border text-white transition-all duration-200',
  {
    variants: {
      variant: {
        info: 'bg-blue-chaos',
        success: 'bg-success-green',
        warning: 'bg-warning-yellow',
        error: 'bg-alert-red',
      },
      size: {
        sm: 'p-3 text-sm',
        md: 'p-4 text-base',
        lg: 'p-5 text-lg',
      },
    },
    defaultVariants: {
      variant: 'info',
      size: 'md',
    },
  }
);

const iconVariants = cva('shrink-0 text-white', {
  variants: {
    variant: {
      info: '',
      success: '',
      warning: '',
      error: '',
    },
    size: {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    },
  },
  defaultVariants: {
    variant: 'info',
    size: 'md',
  },
});

const iconMap = {
  info: Info,
  success: CheckCircle2,
  warning: AlertCircle,
  error: TriangleAlert,
};

export interface NotificationBannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /**
   * The variant of the notification banner
   */
  variant?: 'info' | 'success' | 'warning' | 'error';
  /**
   * The size of the notification banner
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * The title/heading of the notification. Strings render as a styled
   * heading; rich nodes render as-is and bring their own typography.
   */
  title: React.ReactNode;
  /**
   * Optional message/description text
   */
  message?: string;
  /**
   * Whether the banner can be dismissed
   */
  dismissible?: boolean;
  /**
   * Callback fired when the banner is dismissed
   */
  onDismiss?: () => void;
  /**
   * Custom icon to display. If not provided, a default icon based on variant will be used
   */
  icon?: React.ReactNode;
  /**
   * Optional action button/content
   */
  action?: React.ReactNode;
  /**
   * Adds a periodic light sweep across the banner to draw attention.
   * Disabled automatically for users who prefer reduced motion.
   */
  shimmer?: boolean;
}

/**
 * NotificationBanner Component
 *
 * A banner component for displaying important notifications, alerts, or messages.
 * Supports multiple variants (info, success, warning, error) and sizes.
 *
 * Features:
 * - Multiple variants with appropriate colors and icons
 * - Dismissible option with callback
 * - Customizable icon and action button
 * - Accessible with proper ARIA attributes
 * - Responsive design
 */
const NotificationBanner = React.forwardRef<
  HTMLDivElement,
  NotificationBannerProps
>(
  (
    {
      variant = 'error',
      size = 'lg',
      title,
      message,
      dismissible = false,
      onDismiss,
      icon,
      action,
      shimmer = false,
      className,
      ...props
    },
    ref
  ) => {
    const IconComponent = iconMap[variant];
    const defaultIcon = icon ?? (
      <IconComponent
        className={cn(iconVariants({ variant, size }))}
        aria-hidden="true"
      />
    );

    const handleDismiss = React.useCallback(() => {
      onDismiss?.();
    }, [onDismiss]);

    return (
      <div
        ref={ref}
        className={cn(notificationBannerVariants({ variant, size }), className)}
        role="alert"
        aria-live="polite"
        aria-atomic="true"
        {...props}
      >
        {shimmer ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden"
          >
            <span className="motion-safe:animate-banner-shimmer absolute inset-y-0 -left-1/3 hidden w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent motion-safe:block" />
          </span>
        ) : null}
        {/* Full-width row on mobile; shrinks to content and centers on sm+
            (parent `justify-center`) so the text sits in the middle on desktop. */}
        <div className="flex w-full max-w-(--max-width) items-start gap-3 sm:w-auto sm:items-center">
          {/* Icon */}
          <div className="shrink-0 pt-0.5 sm:pt-0">{defaultIcon}</div>
          {/* Content: title + action stack on mobile, inline row on sm+ */}
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <div className="min-w-0">
              {typeof title === 'string' ? (
                <h3 className="font-semibold leading-tight break-words">
                  {title}
                </h3>
              ) : (
                title
              )}
              {message ? (
                <p className="mt-1 leading-relaxed opacity-90 break-words">
                  {message}
                </p>
              ) : null}
            </div>
            {/* Action */}
            {action ? <div className="shrink-0">{action}</div> : null}
          </div>
          {/* Dismiss Button */}
          {dismissible ? (
            <button
              type="button"
              onClick={handleDismiss}
              className={cn(
                'shrink-0 rounded-md p-1 transition-colors',
                'hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                'opacity-70 hover:opacity-100',
                variant === 'info' && 'focus:ring-blue-chaos',
                variant === 'success' && 'focus:ring-success-green',
                variant === 'warning' && 'focus:ring-warning-yellow',
                variant === 'error' && 'focus:ring-alert-red'
              )}
              aria-label="Dismiss notification"
            >
              <X
                className={cn(iconVariants({ variant, size }), 'text-current')}
                aria-hidden="true"
              />
            </button>
          ) : null}
        </div>
      </div>
    );
  }
);

NotificationBanner.displayName = 'NotificationBanner';

export { NotificationBanner, notificationBannerVariants };
